import {
  normalizeApplicationSlug,
  validateApplicationSlug,
} from "./applicationSlug";
import { applicationsAPI, igaAPI, ssoAPI } from "../services/api";
import { instancesAPI } from "../services/instancesAPI";
import { buildSsoPrefillFromWizard } from "./vysorFlows";
import { normalizeEntitlementRiskLevel } from "./entitlementRisk";
import { getRequiredSsoFields } from "./getRequiredSsoFields";
import type {
  Application,
  CreateApplicationRequest,
  CreateEntitlementRequest,
  CreateSSOConfigurationRequest,
  EntitlementType,
  IGAEntitlement,
  SSOPlatform,
} from "../types";

const DEFAULT_DATA_CLASSIFICATION: CreateEntitlementRequest["data_classification"] =
  "Internal";

export type VysorWizardFlowId =
  | "setup-app"
  | "iga-entitlements"
  | "configure-sso";

export { normalizeEntitlementRiskLevel as normalizeEntitlementRisk } from "./entitlementRisk";

/** List page to open after a VYSOR wizard completes successfully. */
export function wizardSuccessRoute(
  flowId: VysorWizardFlowId,
  userRole?: string,
): string {
  switch (flowId) {
    case "setup-app":
      return "/applications";
    case "iga-entitlements":
      return "/applications/iga-onboarding";
    case "configure-sso":
      if (userRole === "super_admin" || userRole === "sso_admin")
        return "/sso-review";
      return "/applications";
    default:
      return "/dashboard";
  }
}

export function wizardSubmitErrorMessage(
  err: unknown,
  fallback: string,
): string {
  const axiosErr = err as {
    response?: { data?: { error?: string; message?: string } };
  };
  return (
    axiosErr.response?.data?.error ||
    axiosErr.response?.data?.message ||
    (err instanceof Error ? err.message : fallback)
  );
}

export async function submitSetupAppWizard(
  payload: Record<string, string>,
  currentUserId?: string,
): Promise<Application> {
  const slug = normalizeApplicationSlug(payload.application_id);
  const slugError = validateApplicationSlug(slug);
  if (slugError) {
    throw new Error(slugError);
  }
  const appData: CreateApplicationRequest = {
    name: payload.name?.trim(),
    application_id: slug,
    business_function: payload.business_function || "",
    description: payload.description?.trim() || "",
    risk_level: payload.risk_level || "Not Defined",
    status: payload.status || "Active",
    primary_owner_ids: currentUserId ? [currentUserId] : [],
    secondary_owner_ids: [],
  };
  return applicationsAPI.createApplication(appData);
}

export async function submitVysorInstanceWizard(
  payload: Record<string, string>,
): Promise<{ id: string; name: string; environment: string }> {
  const applicationId = payload.application_id;
  if (!applicationId) {
    throw new Error("Application is required to create an environment");
  }
  const name = payload.new_instance_name?.trim();
  const environment = payload.new_instance_environment?.trim();
  if (!name) throw new Error("Environment name is required");
  if (!environment) throw new Error("Environment type is required");

  const created = await instancesAPI.createInstance({
    application_id: applicationId,
    name,
    environment,
    description: payload.new_instance_description?.trim() || undefined,
  });
  return {
    id: created.id,
    name: created.name,
    environment: created.environment,
  };
}

export async function submitIgaEntitlementWizard(
  payload: Record<string, string>,
  applicationRiskLevel?: string | null,
): Promise<IGAEntitlement | undefined> {
  const entitlementData: CreateEntitlementRequest = {
    instance_id: payload.instance_id,
    name: payload.name?.trim(),
    description: payload.description?.trim(),
    entitlement_type: (payload.entitlement_type || "Role") as EntitlementType,
    primary_approver_ids: [],
    secondary_approver_ids: [],
    entitlement_owners: [],
    risk_level: normalizeEntitlementRiskLevel(
      payload.risk_level || applicationRiskLevel,
    ),
    data_classification: DEFAULT_DATA_CLASSIFICATION,
    compliance_requirements: [],
    owner_approval_required: false,
    security_approval_required: false,
  };
  const response = await igaAPI.createEntitlement(entitlementData);
  if (!response.data.success) {
    throw new Error(response.data.error || "Failed to create entitlement");
  }
  return response.data.data;
}

export function buildSsoConfigFromWizard(
  collected: Record<string, string>,
  contactEmail?: string,
): CreateSSOConfigurationRequest | null {
  const prefill = buildSsoPrefillFromWizard(collected);
  if (!prefill?.applicationId || !prefill.instanceId || !prefill.sso_type) {
    return null;
  }

  const appName =
    prefill.applicationName || collected.application_name || "Application";
  const platform = (prefill.platform || "generic_saml") as SSOPlatform;
  const notes = collected.metadata_or_notes?.trim() || "";

  const config: CreateSSOConfigurationRequest = {
    application_id: prefill.applicationId,
    instance_id: prefill.instanceId,
    sso_type: prefill.sso_type,
    platform,
    connection_name: `${appName} SSO`.slice(0, 200),
    contact_email: contactEmail || "",
    attributes_to_release: [],
    business_justification:
      prefill.business_justification ||
      notes ||
      `SSO onboarding via VYSOR for ${appName}`,
    data_classification: "internal",
    technical_notes: notes || undefined,
    grant_type: prefill.grant_type || "authorization_code_pkce",
    scopes: prefill.scopes?.length
      ? prefill.scopes
      : ["openid", "profile", "email"],
    redirect_uris: prefill.redirect_uris?.length ? prefill.redirect_uris : [],
    metadata_url: prefill.metadata_url,
    metadata_xml: collected.metadata_content?.trim() || undefined,
    oidc_discovery_endpoint: prefill.oidc_discovery_endpoint,
    client_id: prefill.client_id || collected.client_id?.trim() || undefined,
  };

  if (prefill.sso_type === "SAML") {
    config.entity_id = prefill.entity_id;
    config.acs_url = prefill.acs_url;
    if (collected.metadata_certificate?.includes("BEGIN CERTIFICATE")) {
      config.x509_certificate = collected.metadata_certificate;
    }
  }

  if (prefill.sso_type === "OIDC") {
    if (
      !config.oidc_discovery_endpoint &&
      prefill.metadata_url &&
      /openid-configuration|\.well-known/i.test(prefill.metadata_url)
    ) {
      config.oidc_discovery_endpoint = prefill.metadata_url;
    }
  }

  return config;
}

export async function submitSsoConfigWizard(
  collected: Record<string, string>,
  contactEmail?: string,
): Promise<{ status?: string; connection_name?: string }> {
  const config = buildSsoConfigFromWizard(collected, contactEmail);
  if (!config) {
    throw new Error(
      "Application, environment, and SSO type are required before submitting.",
    );
  }
  if (config.sso_type === "SAML") {
    const entity = String(config.entity_id || "").trim();
    const acs = String(config.acs_url || "").trim();
    const meta = String(
      config.metadata_url || config.metadata_xml || "",
    ).trim();
    if (!(entity && acs) && !meta) {
      throw new Error(
        "Add the app Entity ID and ACS URL (or SAML metadata) before submitting — otherwise the SSO review stays empty.",
      );
    }
  }
  if (config.sso_type === "OIDC") {
    const reqs = getRequiredSsoFields({
      sso_type: "OIDC",
      grant_type: config.grant_type,
      redirect_uris: config.redirect_uris,
      oidc_discovery_endpoint: config.oidc_discovery_endpoint,
      client_id: config.client_id,
    });
    if (!reqs.ready) {
      throw new Error(
        `Add ${reqs.missingLabels.join(" and ") || "required OIDC fields"} before submitting.`,
      );
    }
  }
  const response = await ssoAPI.createConfiguration(config);
  if (!response.data.success) {
    throw new Error(
      response.data.error || "Failed to create SSO configuration",
    );
  }
  return response.data.data || {};
}
